<template>
  <transition name="slide-up" appear>
    <div v-if="isVisible && !settingsOpen" class="gdpr-bar">
      <section class="gdpr-bar__section">
        <div class="gdpr-bar__title">{{ $t('gdpr.banner.title') }}</div>
        <p>{{ $t('gdpr.banner.description') }}</p>
      </section>

      <section class="gdpr-bar__section">
        <button class="gdpr-button" @click="acceptAll">{{ $t('gdpr.banner.accept') }}</button>
        <button class="gdpr-button" @click="settingsOpen = true">
          {{ $t('gdpr.banner.settings') }}
        </button>
      </section>
    </div>
  </transition>

  <transition name="slide-right" appear>
    <aside v-if="settingsOpen" class="gdpr-settings" @click="settingsOpen = false">
      <div class="gdpr-settings__panel" @click.stop>
        <section class="gdpr-settings__section">
          <div class="gdpr-settings__section-top">
            <div class="gdpr-bar__title">
              {{ $t('gdpr.settings.title') }}
            </div>
          </div>

          <p>{{ $t('gdpr.settings.description') }}</p>
        </section>

        <section class="gdpr-settings__section">
          <div class="gdpr-settings__section-top">
            <div class="gdpr-bar__title">
              {{ $t('gdpr.settings.essentials.title') }}
            </div>
          </div>

          <p>{{ $t('gdpr.settings.essentials.description') }}</p>
        </section>

        <section class="gdpr-settings__section">
          <div class="gdpr-settings__section-top">
            <div class="gdpr-bar__title">
              {{ $t('gdpr.settings.analytics.title') }}
            </div>

            <div class="gdpr-switch">
              <input
                id="gtag-switch"
                v-model="gtag"
                name="gtag"
                type="checkbox"
                class="gdpr-switch__field"
              />
              <label for="gtag-switch" class="gdpr-switch__track">
                <span class="gdpr-switch__label">On</span>
                <span class="gdpr-switch__label">Off</span>
              </label>
            </div>
          </div>

          <p>{{ $t('gdpr.settings.analytics.description') }}</p>
        </section>
        <section class="gdpr-settings__section" v-if="hasPixel">
          <div class="gdpr-settings__section-top">
            <div class="gdpr-bar__title">Meta facebook pixel</div>

            <div class="gdpr-switch">
              <input
                id="pixel"
                v-model="pixel"
                name="gtag"
                type="checkbox"
                class="gdpr-switch__field"
              />
              <label for="pixel" class="gdpr-switch__track">
                <span class="gdpr-switch__label">On</span>
                <span class="gdpr-switch__label">Off</span>
              </label>
            </div>
          </div>

          <p>{{ $t('gdpr.settings.analytics.description') }}</p>
        </section>

        <section class="gdpr-section">
          <button class="gdpr-button" @click="saveSettings">{{ $t('gdpr.settings.save') }}</button>
        </section>
      </div>
    </aside>
  </transition>
</template>

<script>
import { addGtag, optIn, optOut } from 'vue-gtag'

let hasLoadedGtag = false

export default {
  name: 'Gdpr',
  props: {
    options: {
      type: Object,
    },
  },
  data() {
    return {
      isVisible: false,
      pixel: false,
      gtag: false,
      all: false,
      settingsOpen: false,
    }
  },
  computed: {
    hasPixel() {
      return import.meta.env.VITE_APP_PIXEL_ID?.length
    },
    colors() {
      if (this.options) {
        let colors = {}
        if (this.options.backgroundColor) colors.backgroundColor = this.options.backgroundColor
        if (this.options.color) colors.color = this.options.color
        if (!this.options.isSolid) colors.backgroundColorAlpha = `${this.options.backgroundColor}E6`
        else colors.backgroundColorAlpha = `${this.options.backgroundColor || '#ffffff'}`
        if (Object.keys(colors).length) return colors
        return null
      }
      return null
    },
  },

  beforeMount() {
    this.setupColors()

    if (localStorage.getItem('gdpr-consent')) {
      this.isVisible = false
    } else {
      setTimeout(() => {
        this.isVisible = true
      }, 3000)
    }

    if (localStorage.getItem('gdpr-pixel')) {
      this.pixel = true
    }

    if (localStorage.getItem('gdpr-gtag')) {
      this.gtag = true
    }
  },

  mounted() {
    if (this.pixel) this.loadPixel()
    if (this.gtag) this.loadGtag()
    if (!this.gtag) {
      this.gtag = true
    }
  },

  methods: {
    setupColors() {
      if (this.colors) {
        if (this.colors.backgroundColor) {
          document
            .querySelector(':root')
            .style.setProperty('--gdpr-bg-color', this.colors.backgroundColor)
        }
        if (this.colors.backgroundColorAlpha) {
          document
            .querySelector(':root')
            .style.setProperty('--gdpr-bg-color-alpha', this.colors.backgroundColorAlpha)
        }
        if (this.colors.color) {
          document.querySelector(':root').style.setProperty('--gdpr-color', this.colors.color)
        }
      }
    },

    saveSettings() {
      if (this.pixel) this.loadPixel()
      else this.disablePixel()

      if (this.gtag) this.loadGtag()
      else this.disableGtag()

      this.settingsOpen = false
      this.isVisible = false

      if (this.pixel || this.gtag) localStorage.setItem('gdpr-consent', '1')
    },
    acceptAll() {
      localStorage.setItem('gdpr-consent', '1')
      this.loadPixel()
      this.loadGtag()
      this.isVisible = false
    },

    loadPixel() {
      if (import.meta.env.VITE_APP_PIXEL_ID) {
        if (window.fbq) {
          const fbq = window.fbq
          fbq('consent', 'grant')
          fbq('init', import.meta.env.VITE_APP_PIXEL_ID)
          fbq('track', 'PageView')
          localStorage.setItem('gdpr-pixel', '1')
        }
      }
    },

    loadGtag() {
      if (import.meta.env.VITE_APP_GA_MEASUREMENT_ID) {
        optIn()

        const loaded = hasLoadedGtag ? Promise.resolve() : addGtag()
        hasLoadedGtag = true

        loaded.then(() => {
          localStorage.setItem('gdpr-gtag', '1')
        })
      }
    },
    disableGtag() {
      optOut()
      localStorage.removeItem('gdpr-gtag')
    },
    disablePixel() {
      if (window.fbq) {
        const fbq = window.fbq
        fbq('consent', 'revoke')
      }
      localStorage.removeItem('gdpr-pixel')
    },
  },
}
</script>

<style lang="scss">
:root {
  --gdpr-bg-color: #ffffff;
  --gdpr-bg-color-alpha: #ffffff90;
  --gdpr-color: black;
}

.gdpr-bar {
  padding-top: 2.75rem;
  padding-bottom: 2.8rem;
  padding-right: 1.875rem;
  padding-left: 1.875rem;
  position: fixed;
  bottom: 0;
  width: 100%;
  background-color: var(--gdpr-bg-color-alpha);
  backdrop-filter: blur(5px);
  color: var(--gdpr-color);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 100;

  @media only screen and (min-width: 568px) {
    padding-right: 2.25rem;
    padding-left: 2.25rem;
  }

  @media only screen and (min-width: 1025px) {
    flex-direction: row;
    height: 200px;
    padding-left: 5.75rem;
    padding-right: 5.75rem;
  }

  @media only screen and (min-width: 1440px) {
    padding-left: calc(50vw - (1440px / 2 - 5.75rem));
    padding-right: calc(50vw - (1440px / 2 - 5.75rem));
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;

    & + & {
      margin-top: 2rem;
    }

    @media only screen and (min-width: 1025px) {
      width: auto;
      & + & {
        margin-top: 0;
        margin-left: 2rem;
      }
    }
  }

  &__title {
    font-size: 28px !important;
    line-height: 32px !important;
    font-weight: 600 !important;
  }
}

.gdpr-settings {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  z-index: 100;

  &__panel {
    padding: 2.75rem 2.15rem;
    width: 100%;
    height: 100%;
    background-color: var(--gdpr-bg-color-alpha);
    backdrop-filter: blur(5px);
    color: var(--gdpr-color);
    display: flex;
    flex-direction: column;
    overflow: auto;

    @media only screen and (min-width: 1025px) {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
      max-width: 38rem;
    }
  }

  &__section {
    margin-bottom: 1.5rem;

    &-top {
      display: flex;
      align-items: center;
      gap: 1rem;
      justify-content: space-between;
      margin-bottom: 0.5rem;
    }
  }
}

.gdpr-button {
  position: relative;
  flex-shrink: 0;
  min-width: 100%;
  height: 2.5rem;
  padding: 0 1rem;
  backface-visibility: hidden;
  white-space: nowrap;
  color: var(--gdpr-color);
  border: 1px solid var(--gdpr-color);
  transition:
    color 150ms linear,
    background-color 150ms linear,
    border-color 150ms linear;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.1em;
  font-size: 0.75rem;

  @media only screen and (min-width: 1025px) {
    min-width: 10rem;
  }

  &:hover {
    color: var(--gdpr-bg-color);
    background-color: var(--gdpr-color);
  }

  &:active {
    color: var(--gdpr-color);
    background-color: transparent;
    border-color: transparent;
    transition: none;
  }
}

.gdpr-switch {
  backface-visibility: hidden;

  &__field {
    display: none;
  }

  &__track {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 2rem;
    padding: 0 1rem;
    color: var(--gdpr-color);
    background-color: transparent;
    border: 1px solid var(--gdpr-color);
    border-radius: 2rem;

    &::after {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      width: calc(50% - 1px);
      height: calc(2rem - 4px);
      background-color: var(--gdpr-color);
      border-radius: calc(2rem - 4px);
      transform: translateZ(0);
      transition: transform 200ms cubic-bezier(0.6, 0.05, 0.01, 0.99);
    }
  }

  &__label + &__label {
    margin-left: 1rem;
  }

  &__field:checked + &__track::after {
    transform: translate3d(100%, 0, 0);
  }
}

.slide-up {
  &-enter-active,
  &-leave-active {
    transition: transform 325ms cb();
  }

  &-leave-from,
  &-enter-to {
    transform: translate3d(0, 0, 0);
  }

  &-enter-from,
  &-leave-to {
    transform: translate3d(0, 100%, 0);
  }
}

.slide-right {
  &-enter-active,
  &-leave-active {
    transition: transform 325ms cb();
  }

  &-leave-from,
  &-enter-to {
    transform: translate3d(0, 0, 0);
  }

  &-enter-from,
  &-leave-to {
    transform: translate3d(-100%, 0, 0);
  }
}
</style>
